import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Card } from '../../components/ui/Card';
import { LineChart, DoughnutChart } from '../../components/charts';
import { 
  Package, 
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Search,
  Download,
  Filter,
  ShoppingBag,
  BarChart2,
  RefreshCw,
  Target,
  Loader2,
  Eye,
  Calendar,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

// Dummy veri oluşturma fonksiyonu
const generateDummyProductData = () => {
  // Popüler ürün isimleri
  const productNames = [
    "iPhone 14 Pro Max 256GB", 
    "Samsung Galaxy S23 Ultra", 
    "MacBook Pro 16\" M2", 
    "AirPods Pro 2. Nesil", 
    "iPad Air 5. Nesil", 
    "Sony WH-1000XM5", 
    "Apple Watch Series 8", 
    "Dyson V15 Detect", 
    "PlayStation 5 Digital", 
    "Nintendo Switch OLED",
    "Logitech MX Master 3S",
    "Samsung 55\" QLED TV",
    "Bose QuietComfort Earbuds",
    "Canon EOS R6",
    "DJI Mini 3 Pro"
  ];
  
  // Kategoriler
  const categories = [
    "Elektronik", 
    "Akıllı Telefonlar", 
    "Bilgisayarlar", 
    "Ses Sistemleri", 
    "Tabletler", 
    "Giyilebilir Teknoloji",
    "Ev Aletleri",
    "Oyun Konsolları",
    "Aksesuarlar",
    "Kameralar"
  ];
  
  // Markalar
  const brands = [
    "Apple", 
    "Samsung", 
    "Sony", 
    "Logitech", 
    "Dyson", 
    "Bose", 
    "Nintendo", 
    "Canon", 
    "DJI", 
    "Microsoft"
  ];
  
  // Dummy ürün verileri oluştur
  return Array.from({ length: 50 }, (_, i) => {
    const revenue = Math.floor(Math.random() * 500000) + 10000;
    const quantity = Math.floor(Math.random() * 500) + 10;
    const price = Math.floor(revenue / quantity);
    
    return {
      id: `PROD-${1000 + i}`,
      name: productNames[i % productNames.length],
      category: categories[Math.floor(Math.random() * categories.length)],
      brand: brands[Math.floor(Math.random() * brands.length)],
      sku: `SKU-${10000 + i}`,
      price: price,
      revenue: revenue,
      quantity: quantity,
      sessions: Math.floor(Math.random() * 10000) + 500,
      addToCart: Math.floor(Math.random() * 2000) + 100,
      transactions: Math.floor(Math.random() * 300) + 5,
      conversionRate: (Math.random() * 5 + 0.5).toFixed(2),
      averagePrice: price,
      revenuePerSession: (revenue / (Math.floor(Math.random() * 10000) + 500)).toFixed(2),
      trend: Math.random() > 0.5 ? Math.random() * 30 : -Math.random() * 30,
      inStock: Math.random() > 0.2,
      rating: (Math.random() * 2 + 3).toFixed(1),
      reviewCount: Math.floor(Math.random() * 500) + 10,
      image: `https://source.unsplash.com/random/100x100?product=${i}`
    };
  });
};

// Dummy kategori verileri oluşturma fonksiyonu
const generateDummyCategoryData = () => {
  const categories = [
    "Akıllı Telefonlar", 
    "Bilgisayarlar", 
    "Ses Sistemleri", 
    "Tabletler", 
    "Giyilebilir Teknoloji",
    "Ev Aletleri",
    "Oyun Konsolları",
    "Aksesuarlar",
    "Kameralar",
    "Televizyonlar"
  ];
  
  return categories.map((category, i) => {
    const revenue = Math.floor(Math.random() * 2000000) + 500000;
    const quantity = Math.floor(Math.random() * 2000) + 100;
    
    return {
      id: `CAT-${100 + i}`,
      name: category,
      revenue: revenue,
      quantity: quantity,
      sessions: Math.floor(Math.random() * 50000) + 5000,
      addToCart: Math.floor(Math.random() * 10000) + 1000,
      transactions: Math.floor(Math.random() * 1000) + 100,
      conversionRate: (Math.random() * 5 + 1).toFixed(2),
      averagePrice: Math.floor(revenue / quantity),
      revenuePerSession: (revenue / (Math.floor(Math.random() * 50000) + 5000)).toFixed(2),
      trend: Math.random() > 0.5 ? Math.random() * 25 : -Math.random() * 25,
      productCount: Math.floor(Math.random() * 100) + 10
    };
  });
};

// Remove initial dummy data for brands as we use API data
// const initialBrands = generateDummyBrandData();

// Initial dummy data generation
const initialProducts = generateDummyProductData();
const initialCategories = generateDummyCategoryData();

export default function ProductPerformance() {
  const [dateRange, setDateRange] = useState('last30');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState(initialProducts);
  const [categories, setCategories] = useState(initialCategories);
  const DEMO_BRAND_ID_SC = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null
  });
  // const [activeTab, setActiveTab] = useState('products'); // Remove activeTab
  const [activeCard, setActiveCard] = useState('total_items_viewed');
  const [selectedMetric, setSelectedMetric] = useState('total_revenue'); // This is for the top card chart
  const [isLoadingMonthlyData, setIsLoadingMonthlyData] = useState(true);
  const [monthlyBrandData, setMonthlyBrandData] = useState({ data: [], totals: {} });
  const [selectedTableMetric, setSelectedTableMetric] = useState('totalRevenue'); // State for table metric
  const [selectedDimension, setSelectedDimension] = useState('Brand'); // State for table dimension
  const [brandSearchTerm, setBrandSearchTerm] = useState(''); // State for brand search
  const [categorySearchTerm, setCategorySearchTerm] = useState(''); // State for category search
  const [productSearchTerm, setProductSearchTerm] = useState(''); // State for product search

  // State for Category Pivot Data (ALL TIME)
  const [isLoadingCategoryPivotDataAll, setIsLoadingCategoryPivotDataAll] = useState(true);
  const [categoryPivotDataAll, setCategoryPivotDataAll] = useState({ data: [], totals: {} });

  // States for Daily Product Data
  const [isLoadingDailyProductData, setIsLoadingDailyProductData] = useState(true);
  const [dailyProductData, setDailyProductData] = useState({ data: [], totals: {}, changes: {} });

  // State for Top 10 Daily Performance Data
  const [isLoadingTop10DailyData, setIsLoadingTop10DailyData] = useState(true);
  const [top10DailyData, setTop10DailyData] = useState([]);

  // State for Pivot Table Product Data (Last 120 Days)
  const [isLoadingProductPivotData, setIsLoadingProductPivotData] = useState(false); // Initially false
  const [productPivotData, setProductPivotData] = useState({ data: [], totals: {} });
  // Daily item-level data for current month (from /daily-item-performance)
  const [isLoadingDailyItemCurrentMonth, setIsLoadingDailyItemCurrentMonth] = useState(false);
  const [dailyItemCurrentMonth, setDailyItemCurrentMonth] = useState([]);
  // Pivot granularity state (monthly | weekly)
  const [pivotGranularity, setPivotGranularity] = useState('monthly');
  // Weekly datasets for Brand and Category
  const [isLoadingWeeklyBrandData, setIsLoadingWeeklyBrandData] = useState(true);
  const [weeklyBrandData, setWeeklyBrandData] = useState({ data: [], totals: {} });
  const [isLoadingWeeklyCategoryData, setIsLoadingWeeklyCategoryData] = useState(true);
  const [weeklyCategoryData, setWeeklyCategoryData] = useState({ data: [], totals: {} });

  // Expanded categories state for hierarchical display (stores expanded category1 names)
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Pagination state for pivot table - start with 50 rows for fast initial load
  const [displayedRowCount, setDisplayedRowCount] = useState(50);
  // Pivot table sorting: key = 'default' | 'name' | 'total' | <month/week key>
  const [pivotSort, setPivotSort] = useState({ key: 'default', dir: 'desc' });
  const ROWS_PER_LOAD = 50; // Load 50 more rows each time

  // Product Insights state
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [productInsights, setProductInsights] = useState(null);
  const [expandedInsightCards, setExpandedInsightCards] = useState(new Set());

  // Brands with GMC hierarchical category support (Category dimension uses GMC hierarchy endpoint)
  const HIERARCHICAL_CATEGORY_BRAND_IDS = new Set([
    '062be473-3b2b-4bad-a2e3-a31995e02d57', // Koton
  ]);

  // Function to handle card click remains the same
  const handleCardClick = (cardId) => {
    setActiveCard(cardId);
    setSelectedMetric(cardId);
  };
  
  // Reset displayed row count when dimension changes
  useEffect(() => {
    setDisplayedRowCount(50);
    setPivotSort({ key: 'default', dir: 'desc' });
  }, [selectedDimension, pivotGranularity]);
  
  // useEffect for dummy data generation remains the same
  useEffect(() => {
    // Simulate data update based on date range (using dummy data)
    console.log(`Date range changed to: ${dateRange}. Regenerating dummy data.`);
    setProducts(generateDummyProductData());
    setCategories(generateDummyCategoryData());
  }, [dateRange]);

  // Fetch monthly brand data ONCE on mount (all available data)
  useEffect(() => {
    const fetchMonthlyItemBrandSummaryData = async () => {
      setIsLoadingMonthlyData(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('No brand ID found in localStorage for monthly summary.');
          setIsLoadingMonthlyData(false);
          return;
        }

        console.log(`Fetching ALL monthly item brand summary for brand: ${brandId}`);
        
        // Always fetch ALL data, ignore date filters
        const apiUrl = `https://api.glowytics.com/api/google-ads/monthly-item-brand-summary?dateRange=all`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Monthly Item Brand Summary Data (ALL):', JSON.stringify(data, null, 2));
        
        setMonthlyBrandData(data);

      } catch (error) {
        console.error('Error fetching monthly item brand summary data:', error);
      } finally {
        setIsLoadingMonthlyData(false);
      }
    };

    fetchMonthlyItemBrandSummaryData();
  }, []); // Only run ONCE on mount

  // Fetch ALL Category Pivot Data ONCE on mount (all available data)
  useEffect(() => {
    const fetchAllCategoryPivotData = async () => {
      setIsLoadingCategoryPivotDataAll(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Category Pivot ALL] No brand ID found.');
          setIsLoadingCategoryPivotDataAll(false);
          return;
        }
        
        // Hierarchical category brands use GMC category hierarchy endpoint (vw_monthly_item_gmc_categories with split categories)
        if (HIERARCHICAL_CATEGORY_BRAND_IDS.has(brandId)) {
          console.log('[Category Pivot ALL] Using GMC category hierarchy endpoint for special brand.');
          // groupBy=full to get all category levels for hierarchical display
          const gmcApiUrl = `https://api.glowytics.com/api/google-ads/monthly-gmc-category-hierarchy?dateRange=all&groupBy=full`;
          
          const gmcResponse = await fetch(gmcApiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Brand-ID': brandId
            },
            credentials: 'include',
          });

          if (!gmcResponse.ok) {
            throw new Error(`[GMC Category Hierarchy] API error: ${gmcResponse.status} ${gmcResponse.statusText}`);
          }

          const gmcData = await gmcResponse.json();
          console.log('[GMC Category Hierarchy] Fetched Data Length (ALL):', gmcData?.data?.length);
          
          // Transform GMC data to match the expected category format
          const transformedData = (gmcData?.data || []).map(item => ({
            year: item.year,
            month: item.month,
            category: item.category || 'Not Set',
            itemCategory: item.category || 'Not Set',
            category1: item.category1,
            category2: item.category2,
            category3: item.category3,
            category4: item.category4,
            category5: item.category5,
            category6: item.category6,
            itemCount: item.itemCount || 0,
            totalRevenue: item.totalRevenue || 0,
            totalItemsViewed: item.totalItemsViewed || 0,
            totalItemsPurchased: item.totalItemsPurchased || 0,
            totalItemsAddedToCart: item.totalItemsAddedToCart || 0
          }));
          
          setCategoryPivotDataAll({ data: transformedData, totals: gmcData?.totals || {} });
          setIsLoadingCategoryPivotDataAll(false);
          return;
        }
        
        console.log(`[Category Pivot ALL] Fetching ALL data for brand: ${brandId}`);
        
        // Always fetch ALL data, ignore date filters
        const apiUrl = `https://api.glowytics.com/api/google-ads/monthly-category-summary?dateRange=all`;
        
        const response = await fetch(apiUrl, { 
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`[Category Pivot ALL] API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[Category Pivot ALL] Fetched Data Length (ALL):', data?.data?.length);
        setCategoryPivotDataAll(data);

      } catch (error) {
        console.error('[Category Pivot ALL] Error fetching data:', error);
        setCategoryPivotDataAll({ data: [], totals: {} });
      } finally {
        setIsLoadingCategoryPivotDataAll(false);
      }
    };

    fetchAllCategoryPivotData();
  }, []); // Only run ONCE on mount

  // Placeholder fetch helpers for new views (not invoked yet)
  const fetchWeeklyItemBrandSummary = async (dateParam = 'last12') => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return null;
    const url = `https://api.glowytics.com/api/google-ads/weekly-item-brand-summary?dateRange=${dateParam}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`[weekly-item-brand-summary] ${res.status}`);
    return res.json();
  };

  // --- Fetch ALL Weekly Brand Summary Data ONCE on mount ---
  useEffect(() => {
    const fetchAllWeeklyBrand = async () => {
      setIsLoadingWeeklyBrandData(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Weekly Brand] No brand ID found.');
          setWeeklyBrandData({ data: [], totals: {} });
          return;
        }
        const apiUrl = `https://api.glowytics.com/api/google-ads/weekly-item-brand-summary?dateRange=all`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`[Weekly Brand] API error: ${response.status} ${response.statusText}`);
        const data = await response.json();
        console.log('[Weekly Brand] Fetched Data Length (ALL):', data?.data?.length);
        setWeeklyBrandData(data);
      } catch (e) {
        console.error('[Weekly Brand] Error:', e);
        setWeeklyBrandData({ data: [], totals: {} });
      } finally {
        setIsLoadingWeeklyBrandData(false);
      }
    };
    fetchAllWeeklyBrand();
  }, []);

  // --- Fetch ALL Weekly Category Performance Data ONCE on mount ---
  useEffect(() => {
    const fetchAllWeeklyCategory = async () => {
      setIsLoadingWeeklyCategoryData(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Weekly Category] No brand ID found.');
          setWeeklyCategoryData({ data: [], totals: {} });
          return;
        }
        const apiUrl = `https://api.glowytics.com/api/google-ads/weekly-category-performance?dateRange=all`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`[Weekly Category] API error: ${response.status} ${response.statusText}`);
        const data = await response.json();
        console.log('[Weekly Category] Fetched Data Length (ALL):', data?.data?.length);
        setWeeklyCategoryData(data);
      } catch (e) {
        console.error('[Weekly Category] Error:', e);
        setWeeklyCategoryData({ data: [], totals: {} });
      } finally {
        setIsLoadingWeeklyCategoryData(false);
      }
    };
    fetchAllWeeklyCategory();
  }, []);

  const fetchWeeklyCategoryPerformance = async (dateParam = 'last12') => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return null;
    const url = `https://api.glowytics.com/api/google-ads/weekly-category-performance?dateRange=${dateParam}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`[weekly-category-performance] ${res.status}`);
    return res.json();
  };

  const fetchMonthlyItemPerformance = async (dateParam = 'last12') => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return null;
    const url = `https://api.glowytics.com/api/google-ads/monthly-item-performance?dateRange=${dateParam}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`[monthly-item-performance] ${res.status}`);
    return res.json();
  };

  // --- Fetch and Log Top 10 Daily Performance Data (ONCE on mount) ---
  // UPDATED: Now depends on dateRange and fetches accordingly
  useEffect(() => {
    const fetchAndLogTop10Daily = async () => {
      setIsLoadingTop10DailyData(true); // Set loading true
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Top 10 Daily] No brand ID found.');
          setIsLoadingTop10DailyData(false); // Set loading false
          setTop10DailyData([]); // Clear data
          return;
        }
        // Now include dateRange in the log and fetch URL
        console.log(`[Top 10 Daily] Fetching data for brand: ${brandId}, range: ${dateRange}`);

        // Build URL with custom date parameters if needed
        let apiUrl = `https://api.glowytics.com/api/google-ads/top10-daily-performance?dateRange=${dateRange}`;
        if (dateRange === 'custom' && customDateRange.startDate && customDateRange.endDate) {
          apiUrl += `&startDate=${customDateRange.startDate}&endDate=${customDateRange.endDate}`;
        }
        
        const response = await fetch(apiUrl, { // Add dateRange parameter
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`[Top 10 Daily] API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        // console.log('[Top 10 Daily] Fetched Data:', JSON.stringify(data, null, 2));
        setTop10DailyData(Array.isArray(data) ? data : []); // Update state

      } catch (error) {
        console.error('[Top 10 Daily] Error fetching data:', error);
        setTop10DailyData([]); // Clear data on error
      } finally {
        setIsLoadingTop10DailyData(false); // Set loading false
      }
    };

    fetchAndLogTop10Daily();
  }, [dateRange, customDateRange.startDate, customDateRange.endDate]); // Add dateRange and custom dates to dependency array

  // Fetch Daily Product Performance Data
  useEffect(() => {
    const fetchDailyProductPerformanceData = async () => {
      setIsLoadingDailyProductData(true); 
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('No brand ID found in localStorage for daily product performance.');
          setIsLoadingDailyProductData(false);
          return;
        }
        console.log(`Fetching daily product performance for brand: ${brandId}, range: ${dateRange}`);
        
        // Build URL with custom date parameters if needed
        let apiUrl = `https://api.glowytics.com/api/google-ads/daily-product-performance?dateRange=${dateRange}`;
        if (dateRange === 'custom' && customDateRange.startDate && customDateRange.endDate) {
          apiUrl += `&startDate=${customDateRange.startDate}&endDate=${customDateRange.endDate}`;
        }
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
         // --- CONSOLE LOG THE FETCHED DATA --- 
        console.log(`[fetchDailyProductPerformanceData] Received data for range: ${dateRange}:`, JSON.stringify(data, null, 2)); 
        console.log('Fetched Daily Product Performance Data:', JSON.stringify(data, null, 2)); 
        
        setDailyProductData(data);

      } catch (error) {
        console.error('Error fetching daily product performance data:', error);
        // Set empty state on error
        setDailyProductData({ data: [], totals: {} }); 
      } finally {
        setIsLoadingDailyProductData(false);
      }
    };

    fetchDailyProductPerformanceData();
  }, [dateRange, customDateRange.startDate, customDateRange.endDate]); // Re-run effect when dateRange or custom dates change

  // --- Fetch Product Pivot Data ONCE on mount (all available data including current month) ---
  useEffect(() => {
    const fetchProductPivotDataForAllTime = async () => {
      setIsLoadingProductPivotData(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Product Pivot Data] No brand ID found.');
          setIsLoadingProductPivotData(false);
          setProductPivotData({ data: [], totals: {} });
          return;
        }
        console.log(
          `[Product Pivot Data] Fetching ALL monthly data (including current month) for brand: ${brandId}`
        );
        
        // Fetch ALL months including current month - vw_monthly_item_performance handles month boundaries correctly
        const apiUrl = `https://api.glowytics.com/api/google-ads/monthly-item-performance?dateRange=all`;
        
        const response = await fetch(apiUrl, { 
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`[Product Pivot Data] API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log(
          '[Product Pivot Data] Fetched Monthly Item Performance Data Length (ALL months):',
          data?.data?.length
        );
        setProductPivotData(data);

      } catch (error) {
        console.error('[Product Pivot Data] Error fetching data:', error);
        setProductPivotData({ data: [], totals: {} });
      } finally {
        setIsLoadingProductPivotData(false);
      }
    };

    fetchProductPivotDataForAllTime();
  }, []); // Only run ONCE on mount

  // --- Fetch Item Performance Summary for the current month (aggregated, fast) ---
  useEffect(() => {
    const fetchItemPerformanceSummaryCurrentMonth = async () => {
      setIsLoadingDailyItemCurrentMonth(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          console.log('[Item Performance Summary] No brand ID found.');
          setDailyItemCurrentMonth([]);
          setIsLoadingDailyItemCurrentMonth(false);
          return;
        }
        // Use new aggregated endpoint - much faster than daily-item-performance
        // Returns product-level summary instead of millions of daily rows
        const apiUrl =
          'https://api.glowytics.com/api/google-ads/item-performance-summary?dateRange=last30&limit=50000';

        console.log(
          `[Item Performance Summary] Fetching item performance summary (last30) for brand: ${brandId}`
        );

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(
            `[Item Performance Summary] API error: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1; // 1-based

        // Transform aggregated data to match expected format for pivot table
        // Add year/month fields for compatibility with pivot logic
        const transformedRows = (Array.isArray(data) ? data : []).map(row => ({
          ...row,
          year: currentYear,
          month: currentMonth,
          // Ensure field names match what pivot expects
          itemName: row.itemName || row.item_name,
          itemBrand: row.itemBrand || row.item_brand,
          itemCategory: row.itemCategory || row.item_category,
          totalRevenue: row.totalRevenue || row.total_revenue || 0,
          totalItemsViewed: row.totalItemsViewed || row.total_items_viewed || 0,
          totalItemsPurchased: row.totalItemsPurchased || row.total_items_purchased || 0,
          totalItemsAddedToCart: row.totalItemsAddedToCart || row.total_items_added_to_cart || 0
        }));

        console.log(
          `[Item Performance Summary] Loaded ${transformedRows.length} product summaries for current month`
        );

        setDailyItemCurrentMonth(transformedRows);
      } catch (error) {
        console.error('[Item Performance Summary] Error fetching data:', error);
        setDailyItemCurrentMonth([]);
      } finally {
        setIsLoadingDailyItemCurrentMonth(false);
      }
    };

    fetchItemPerformanceSummaryCurrentMonth();
  }, []);

  // Fetch Product Insights (mount-only)
  useEffect(() => {
    const fetchProductInsights = async () => {
      try {
        setIsLoadingInsights(true);
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;

        const response = await fetch('https://api.glowytics.com/api/google-ads/product-insights', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setProductInsights(data);
      } catch (error) {
        console.error('[Product Insights] Error fetching data:', error);
        setProductInsights(null);
      } finally {
        setIsLoadingInsights(false);
      }
    };

    fetchProductInsights();
  }, []);

  // Formatting functions
  const formatCurrency = (value, maximumFractionDigits = 2) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      maximumFractionDigits: maximumFractionDigits
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR').format(value);
  };

  // Compact currency formatter for table cells (no cents, uses K/M suffix)
  const formatCurrencyCompact = (value) => {
    if (value == null) return '-';
    const absVal = Math.abs(value);
    if (absVal >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M ₺`;
    }
    if (absVal >= 1000) {
      return `${(value / 1000).toFixed(1)}K ₺`;
    }
    // For small numbers, no fraction digits
    return `${Math.round(value)} ₺`;
  };

  // Helper function to get chart data for the selected metric
  const getChartDataForMetric = (metric) => {
    // Generate some sample data points for the chart
    const getRandomData = (baseValue, variancePercent = 15, count = 30) => {
      return Array.from({ length: count }, () => {
        const randomVariance = (Math.random() * variancePercent * 2) - variancePercent;
        return baseValue * (1 + randomVariance / 100);
      });
    };
    
    // Calculate average values for each metric for the base value
    const totalRevenue = products.reduce((sum, p) => sum + p.revenue, 0);
    const totalUnits = products.reduce((sum, p) => sum + p.quantity, 0);
    const totalTransactions = products.reduce((sum, p) => sum + p.transactions, 0);
    const avgOrderValue = totalRevenue > 0 && totalTransactions > 0 ? totalRevenue / totalTransactions : 0; // Corrected AOV calculation
    const avgPrice = products.length > 0 ? products.reduce((sum, p) => sum + p.averagePrice, 0) / products.length : 0;
    const totalSessions = products.reduce((sum, p) => sum + p.sessions, 0);
    const conversionRate = totalSessions > 0 ? (totalTransactions / totalSessions) * 100 : 0;
    
    // Base values per day (dividing by 30 for daily average)
    const daysInPeriod = dateRange === 'last7' ? 7 : dateRange === 'last90' ? 90 : 30;
    const dailyRevenue = totalRevenue / daysInPeriod;
    const dailyUnits = totalUnits / daysInPeriod;
    const dailyAvgOrder = avgOrderValue; // AOV is usually not daily
    const dailyPrice = avgPrice; // Avg Price is usually not daily
    const dailyConvRate = conversionRate; // Conv Rate is usually not daily
    
    // Generate labels for the selected date range
    const labels = Array.from({ length: daysInPeriod }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (daysInPeriod - i - 1));
      return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    });
    
    switch(metric) {
      case 'total_revenue':
        return {
          label: 'Revenue',
          data: getRandomData(dailyRevenue, 15, daysInPeriod),
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: true,
          tension: 0.4
        };
      case 'total_products': // This metric doesn't make sense for a daily trend, using total units instead
        return {
          label: 'Units Sold',
          data: getRandomData(dailyUnits, 20, daysInPeriod),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4
        };
      case 'units_sold':
        return {
          label: 'Units Sold',
          data: getRandomData(dailyUnits, 20, daysInPeriod),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4
        };
      case 'avg_order_value':
        return {
          label: 'Avg. Order Value',
          data: getRandomData(dailyAvgOrder, 10, daysInPeriod),
          borderColor: 'rgb(139, 92, 246)',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          fill: true,
          tension: 0.4
        };
      case 'conversion_rate':
        return {
          label: 'Conversion Rate (%)',
          data: getRandomData(dailyConvRate, 20, daysInPeriod),
          borderColor: 'rgb(79, 70, 229)',
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          fill: true,
          tension: 0.4
        };
      case 'avg_price':
        return {
          label: 'Avg. Price',
          data: getRandomData(dailyPrice, 8, daysInPeriod),
          borderColor: 'rgb(236, 72, 153)',
          backgroundColor: 'rgba(236, 72, 153, 0.1)',
          fill: true,
          tension: 0.4
        };
      default:
        return {
          label: 'Revenue',
          data: getRandomData(dailyRevenue, 15, daysInPeriod),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        };
    }
  };

  // --- Memoized Chart Data using REAL Daily Data --- 
  const revenueTrendChartData = useMemo(() => {
    console.log(`[revenueTrendChartData] Calculating for metric: ${selectedMetric}, range: ${dateRange}`);
    console.log(`[revenueTrendChartData] Input dailyProductData.data (first 5):`, dailyProductData?.data?.slice(0, 5));

    // Return empty structure while loading or if no data
    if (isLoadingDailyProductData || !dailyProductData || !dailyProductData.data || dailyProductData.data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Sort data by date just in case it's not guaranteed
    const sortedData = [...dailyProductData.data].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Extract labels (dates) from the actual sorted data
    const labels = sortedData.map(item => 
      new Date(item.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
    );

    // Generate dataset based on selectedMetric and sortedData
    let metricData = [];
    let datasetLabel = 'Unknown';
    switch (selectedMetric) {
      case 'total_items_viewed':
        metricData = sortedData.map(item => item.totalItemsViewed);
        datasetLabel = 'Items Viewed';
        break;
      case 'total_revenue':
        metricData = sortedData.map(item => item.totalRevenue);
        datasetLabel = 'Revenue';
        break;
      case 'avg_order_value':
        // Placeholder: Using daily revenue as AOV is not typically a daily metric
        metricData = sortedData.map(item => item.totalRevenue);
        datasetLabel = 'Revenue (for AOV card)';
        break;
      case 'units_sold':
        metricData = sortedData.map(item => item.totalItemsPurchased);
        datasetLabel = 'Units Sold';
        break;
      case 'conversion_rate':
        metricData = sortedData.map(item => 
          item.totalItemsViewed > 0 ? (item.totalItemsPurchased / item.totalItemsViewed) * 100 : 0
        );
        datasetLabel = 'Conversion Rate (%)';
        break;
      default:
        metricData = sortedData.map(item => item.totalRevenue);
        datasetLabel = 'Revenue';
        break;
    }

    // Define colors based on metric (consistent with cards)
    const colors = {
      total_items_viewed: { border: 'rgb(8, 145, 178)', bg: 'rgba(8, 145, 178, 0.1)' }, // Teal
      total_revenue:    { border: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)' }, // Orange
      avg_order_value:  { border: 'rgb(139, 92, 246)', bg: 'rgba(139, 92, 246, 0.1)' }, // Purple
      units_sold:       { border: 'rgb(16, 185, 129)', bg: 'rgba(16, 185, 129, 0.1)' }, // Green
      conversion_rate:  { border: 'rgb(79, 70, 229)', bg: 'rgba(79, 70, 229, 0.1)' }, // Indigo
    };
    const selectedColor = colors[selectedMetric] || colors.total_revenue; // Default to revenue color

    // Construct the final dataset object
    const dataset = {
      label: datasetLabel,
      data: metricData,
      borderColor: selectedColor.border,
      backgroundColor: selectedColor.bg,
      fill: true,
      tension: 0.4
    };

    return {
      labels: labels, // Uses correctly generated labels
      datasets: [dataset] // Use the dataset generated from real data
    };
  // Dependencies: Recalculate when metric changes, data loads/changes, or dateRange changes (which triggers data refetch)
  }, [selectedMetric, dateRange, dailyProductData, isLoadingDailyProductData]); 

  // --- Data Transformation for Pivot Table --- 
  const pivotTableData = useMemo(() => {
    const currentBrandId =
      typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
    const isHierarchicalCategoryBrand = HIERARCHICAL_CATEGORY_BRAND_IDS.has(currentBrandId);
    // Determine the source data based on the selected dimension
    let sourceData;
    let isLoading;
    if (isHierarchicalCategoryBrand && selectedDimension === 'Category') {
      // Hierarchical category brands use GMC category hierarchy for Category dimension
      sourceData = categoryPivotDataAll?.data;
      isLoading = isLoadingCategoryPivotDataAll;
    } else if (selectedDimension === 'Brand') {
      if (pivotGranularity === 'weekly') {
        sourceData = weeklyBrandData?.data;
        isLoading = isLoadingWeeklyBrandData;
      } else {
        sourceData = monthlyBrandData?.data;
        isLoading = isLoadingMonthlyData;
      }
    } else if (selectedDimension === 'Category') {
      if (pivotGranularity === 'weekly') {
        sourceData = weeklyCategoryData?.data;
        isLoading = isLoadingWeeklyCategoryData;
      } else {
        sourceData = categoryPivotDataAll?.data;
        isLoading = isLoadingCategoryPivotDataAll;
      }
    } else if (selectedDimension === 'Product') {
      sourceData = productPivotData?.data || [];
      isLoading = isLoadingProductPivotData;
    } else {
      sourceData = [];
      isLoading = false;
    }

    if (isLoading || !sourceData || sourceData.length === 0) {
      // Return empty structure but indicate loading state if applicable
      return { months: [], dimensionData: {}, monthlyTotals: {}, dimensionTotals: {}, isLoading }; 
    }

    const monthsSet = new Set(); // For weekly, we will store keys like 'YYYY-Www'
    const dimensionData = {}; // Generic name for brand/category/product data
    const monthlyTotals = {};
    const dimensionTotals = {}; // Object to store totals for each dimension item

    sourceData.forEach(item => {
      let dimensionName;
      let monthDate;
      let revenue = 0;
      let purchases = 0;
      let views = 0;
      let addedToCart = 0;
      let monthString; // For weekly mode, this will be 'YYYY-Www'

      // Extract data based on dimension
      const isGaEcomRow = typeof item.itemRevenue !== 'undefined';

      if (selectedDimension === 'Brand') {
        dimensionName = item.itemBrand ? item.itemBrand.trim() : 'Unknown Brand';
        // Demo brand: manipulate brand naming slightly while keeping metrics same
        try {
          const currentBrandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
          if (currentBrandId === DEMO_BRAND_ID_SC && dimensionName && !dimensionName.startsWith('Unknown')) {
            const suffixes = [' Ultra', ' Flex', ' Pro', ' Max', ' Elite', ' Trail', ' Air'];
            const idx = dimensionName.length % suffixes.length;
            const ensured = /^skechers/i.test(dimensionName) ? dimensionName : `Skechers ${dimensionName}`;
            dimensionName = `${ensured}${suffixes[idx]}`;
          }
        } catch (_) {}
        if (dimensionName.startsWith('Unknown') && !item.totalRevenue && !item.totalItemsPurchased && !item.totalItemsViewed && !item.itemRevenue) return;

        if (isGaEcomRow) {
          // GA e-commerce monthly row (no weekly granularity: always use year/month)
          const year = item.year;
          const month = item.month;
          if (!year || !month || isNaN(year) || isNaN(month) || month < 1 || month > 12) return;
          revenue = item.itemRevenue || 0;
          purchases = item.itemsPurchased || 0;
          views = item.itemsViewed || 0;
          addedToCart = item.itemAddToCart || 0;
          monthString = `${year}-${month.toString().padStart(2, '0')}`;
          monthsSet.add(monthString);
        } else if (pivotGranularity === 'weekly') {
          // weekly brand: use year/weekNumber
          revenue = item.totalRevenue || 0;
          purchases = item.totalItemsPurchased || 0;
          views = item.totalItemsViewed || 0;
          addedToCart = item.totalItemsAddedToCart || 0;
          const year = item.year;
          const week = item.weekNumber;
          if (!year || !week || isNaN(year) || isNaN(week)) return;
          monthString = `${year}-W${week.toString().padStart(2, '0')}`;
          monthsSet.add(monthString);
        } else {
          // monthly brand
          monthDate = new Date(item.monthStart); 
          revenue = item.totalRevenue || 0;
          purchases = item.totalItemsPurchased || 0;
          views = item.totalItemsViewed || 0;
          addedToCart = item.totalItemsAddedToCart || 0;
        }

      } else if (selectedDimension === 'Category') {
        dimensionName = item.itemCategory ? item.itemCategory.trim() : 'Unknown Category';
        
        if (dimensionName.startsWith('Unknown') && !item.totalRevenue && !item.totalItemsPurchased && !item.totalItemsViewed && !item.itemRevenue) return;
        if (isGaEcomRow) {
          // GA e-commerce monthly row (always year/month)
          const year = item.year;
          const month = item.month;
          if (!year || !month || isNaN(year) || isNaN(month) || month < 1 || month > 12) {
            console.warn('[PivotTableData] Invalid year/month encountered for GA category item:', item);
            return;
          }
          revenue = item.itemRevenue || 0;
          purchases = item.itemsPurchased || 0;
          views = item.itemsViewed || 0;
          addedToCart = item.itemAddToCart || 0;
          monthString = `${year}-${month.toString().padStart(2, '0')}`;
          monthsSet.add(monthString);
        } else if (pivotGranularity === 'weekly') {
          const year = item.year;
          const week = item.weekNumber;
          if (!year || !week || isNaN(year) || isNaN(week)) return;
          revenue = item.totalRevenue || 0;
          purchases = item.totalItemsPurchased || 0;
          views = item.totalItemsViewed || 0;
          addedToCart = item.totalItemsAddedToCart || 0;
          monthString = `${year}-W${week.toString().padStart(2, '0')}`;
          monthsSet.add(monthString);
        } else {
          // monthly category
          const year = item.year;
          const month = item.month;
          if (!year || !month || isNaN(year) || isNaN(month) || month < 1 || month > 12) {
            console.warn('[PivotTableData] Invalid year/month encountered for category item:', item);
            return; // Skip this item
          }
          revenue = item.totalRevenue || 0;
          purchases = item.totalItemsPurchased || 0;
          views = item.totalItemsViewed || 0;
          addedToCart = item.totalItemsAddedToCart || 0;
          monthString = `${year}-${month.toString().padStart(2, '0')}`;
          monthsSet.add(monthString);
        }
        
      } else if (selectedDimension === 'Product') {
        dimensionName = item.itemName ? item.itemName.trim() : 'Unknown Product';
        
        if (dimensionName.startsWith('Unknown') && !item.totalRevenue && !item.totalItemsPurchased && !item.totalItemsViewed && !item.itemRevenue) return;

        // Directly use year and month from the item for Product
        const year = item.year;
        const month = item.month;

        // Validate year and month before creating monthString
        if (!year || !month || isNaN(year) || isNaN(month) || month < 1 || month > 12) {
          console.warn('[PivotTableData] Invalid year/month encountered for product item:', item);
          return; // Skip this item
        }

        if (isGaEcomRow) {
          revenue = item.itemRevenue || 0;
          purchases = item.itemsPurchased || 0;
          views = item.itemsViewed || 0;
          addedToCart = item.itemAddToCart || 0;
        } else {
          revenue = item.totalRevenue || 0;
          purchases = item.totalItemsPurchased || 0;
          views = item.totalItemsViewed || 0;
          addedToCart = item.totalItemsAddedToCart || 0;
        }

        // Create monthString directly
        monthString = `${year}-${month.toString().padStart(2, '0')}`;
        monthsSet.add(monthString);

      } else {
        return; // Should not happen
      }

      // --- Create monthString for Brand if not already done (only for non-GA rows) ---
      if (selectedDimension === 'Brand' && pivotGranularity !== 'weekly' && !isGaEcomRow) {
        // Ensure monthDate is valid before proceeding
        if (isNaN(monthDate.getTime())) {
          console.warn(`[PivotTableData] Invalid monthStart date encountered for ${selectedDimension} item:`, item);
          return; // Skip this item if the date is invalid
        }
        monthString = `${monthDate.getFullYear()}-${(monthDate.getMonth() + 1).toString().padStart(2, '0')}`;
        monthsSet.add(monthString);
      }
      // --- End monthString creation ---

      // Initialize structures if they don't exist
      if (!dimensionData[dimensionName]) {
        dimensionData[dimensionName] = {};
      }
      if (!dimensionData[dimensionName][monthString]) {
        dimensionData[dimensionName][monthString] = {
          totalRevenue: 0,
          totalItemsPurchased: 0,
          totalItemsViewed: 0,
          totalItemsAddedToCart: 0,
        };
      }
      if (!dimensionTotals[dimensionName]) {
        dimensionTotals[dimensionName] = {
          totalRevenue: 0,
          totalItemsPurchased: 0,
          totalItemsViewed: 0,
          totalItemsAddedToCart: 0,
        };
      }
      if (!monthlyTotals[monthString]) {
        monthlyTotals[monthString] = {
          totalRevenue: 0,
          totalItemsPurchased: 0,
          totalItemsViewed: 0,
          totalItemsAddedToCart: 0,
        };
      }

      // Aggregate data for the specific dimension and month
      dimensionData[dimensionName][monthString].totalRevenue += revenue;
      dimensionData[dimensionName][monthString].totalItemsPurchased += purchases;
      dimensionData[dimensionName][monthString].totalItemsViewed += views;
      dimensionData[dimensionName][monthString].totalItemsAddedToCart += addedToCart;

      // Aggregate totals for the dimension (across all months in the source data)
      // Note: For 'Product', this aggregates daily data within the dateRange
      dimensionTotals[dimensionName].totalRevenue += revenue;
      dimensionTotals[dimensionName].totalItemsPurchased += purchases;
      dimensionTotals[dimensionName].totalItemsViewed += views;
      dimensionTotals[dimensionName].totalItemsAddedToCart += addedToCart;

      // Aggregate monthly totals (across all dimensions)
      monthlyTotals[monthString].totalRevenue += revenue;
      monthlyTotals[monthString].totalItemsPurchased += purchases;
      monthlyTotals[monthString].totalItemsViewed += views;
      monthlyTotals[monthString].totalItemsAddedToCart += addedToCart;
    });

    const sortedMonths = Array.from(monthsSet).sort();

    // --- Calculate derived metrics (like conversion rate) after aggregation ---

    // Monthly Totals Conversion Rate
    Object.keys(monthlyTotals).forEach(month => {
      monthlyTotals[month].conversionRate = monthlyTotals[month].totalItemsViewed > 0
        ? (monthlyTotals[month].totalItemsPurchased / monthlyTotals[month].totalItemsViewed)
        : 0;
    });

    // Dimension Totals Conversion Rate
    Object.keys(dimensionTotals).forEach(dimName => {
      dimensionTotals[dimName].conversionRate = dimensionTotals[dimName].totalItemsViewed > 0
        ? (dimensionTotals[dimName].totalItemsPurchased / dimensionTotals[dimName].totalItemsViewed)
        : 0;
    });
    
    // Monthly Dimension Data Conversion Rate
    Object.keys(dimensionData).forEach(dimName => {
        Object.keys(dimensionData[dimName]).forEach(month => {
            const monthData = dimensionData[dimName][month];
            monthData.conversionRate = monthData.totalItemsViewed > 0
                ? (monthData.totalItemsPurchased / monthData.totalItemsViewed)
                : 0;
              // Add-to-Cart Rate: add_to_cart / views
              monthData.addToCartRate = monthData.totalItemsViewed > 0
                  ? (monthData.totalItemsAddedToCart / monthData.totalItemsViewed)
                  : 0;
        });
    });

    // Log the sorted months when Product dimension is selected
    if (selectedDimension === 'Product') {
      console.log('[PivotTableData] Sorted Months for Product:', sortedMonths);
    }

    // Apply search filter for all dimensions
    let filteredDimensionData = dimensionData;
    let filteredDimensionTotals = dimensionTotals;
    
    let currentSearchTerm = '';
    if (selectedDimension === 'Brand') {
      currentSearchTerm = brandSearchTerm;
    } else if (selectedDimension === 'Category') {
      currentSearchTerm = categorySearchTerm;
    } else if (selectedDimension === 'Product') {
      currentSearchTerm = productSearchTerm;
    }
    
    if (currentSearchTerm.trim()) {
      const searchTerm = currentSearchTerm.toLowerCase().trim();
      
      // Filter dimension data
      filteredDimensionData = {};
      filteredDimensionTotals = {};
      
      Object.entries(dimensionData).forEach(([dimensionName, monthlyData]) => {
        if (dimensionName.toLowerCase().includes(searchTerm)) {
          filteredDimensionData[dimensionName] = monthlyData;
          filteredDimensionTotals[dimensionName] = dimensionTotals[dimensionName];
        }
      });
    }

    // Recompute monthly totals based on the filtered dimension data so the footer reflects filters
    const filteredMonthlyTotals = {};
    Object.values(filteredDimensionData).forEach(monthlyMap => {
      Object.entries(monthlyMap).forEach(([month, metrics]) => {
        if (!filteredMonthlyTotals[month]) {
          filteredMonthlyTotals[month] = {
            totalRevenue: 0,
            totalItemsPurchased: 0,
            totalItemsViewed: 0,
            totalItemsAddedToCart: 0,
          };
        }
        filteredMonthlyTotals[month].totalRevenue += metrics.totalRevenue || 0;
        filteredMonthlyTotals[month].totalItemsPurchased += metrics.totalItemsPurchased || 0;
        filteredMonthlyTotals[month].totalItemsViewed += metrics.totalItemsViewed || 0;
        filteredMonthlyTotals[month].totalItemsAddedToCart += metrics.totalItemsAddedToCart || 0;
      });
    });

    // Calculate conversion rate for filtered monthly totals
    Object.keys(filteredMonthlyTotals).forEach(month => {
      const monthData = filteredMonthlyTotals[month];
      monthData.conversionRate = monthData.totalItemsViewed > 0
        ? (monthData.totalItemsPurchased / monthData.totalItemsViewed)
        : 0;
      monthData.addToCartRate = monthData.totalItemsViewed > 0
        ? (monthData.totalItemsAddedToCart / monthData.totalItemsViewed)
        : 0;
    });

    // Build hierarchical category structure for special brand
    let hierarchicalCategoryData = null;
    if (isHierarchicalCategoryBrand && selectedDimension === 'Category' && sourceData && sourceData.length > 0) {
      // Group by category1, then aggregate category2 within each
      const hierarchy = {};
      
      sourceData.forEach(item => {
        const cat1 = item.category1 || 'Not Set';
        const cat2 = item.category2 || null;
        const year = item.year;
        const month = item.month;
        if (!year || !month || isNaN(year) || isNaN(month) || month < 1 || month > 12) return;
        
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        const revenue = item.totalRevenue || 0;
        const purchases = item.totalItemsPurchased || 0;
        const views = item.totalItemsViewed || 0;
        const addedToCart = item.totalItemsAddedToCart || 0;
        
        // Initialize category1
        if (!hierarchy[cat1]) {
          hierarchy[cat1] = {
            months: {},
            totals: { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 },
            children: {}
          };
        }
        
        // Initialize month for category1
        if (!hierarchy[cat1].months[monthKey]) {
          hierarchy[cat1].months[monthKey] = { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 };
        }
        
        // Aggregate to category1 level
        hierarchy[cat1].months[monthKey].totalRevenue += revenue;
        hierarchy[cat1].months[monthKey].totalItemsPurchased += purchases;
        hierarchy[cat1].months[monthKey].totalItemsViewed += views;
        hierarchy[cat1].months[monthKey].totalItemsAddedToCart += addedToCart;
        hierarchy[cat1].totals.totalRevenue += revenue;
        hierarchy[cat1].totals.totalItemsPurchased += purchases;
        hierarchy[cat1].totals.totalItemsViewed += views;
        hierarchy[cat1].totals.totalItemsAddedToCart += addedToCart;
        
        // If there's a category2, add as child
        if (cat2) {
          if (!hierarchy[cat1].children[cat2]) {
            hierarchy[cat1].children[cat2] = {
              months: {},
              totals: { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 }
            };
          }
          if (!hierarchy[cat1].children[cat2].months[monthKey]) {
            hierarchy[cat1].children[cat2].months[monthKey] = { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 };
          }
          hierarchy[cat1].children[cat2].months[monthKey].totalRevenue += revenue;
          hierarchy[cat1].children[cat2].months[monthKey].totalItemsPurchased += purchases;
          hierarchy[cat1].children[cat2].months[monthKey].totalItemsViewed += views;
          hierarchy[cat1].children[cat2].months[monthKey].totalItemsAddedToCart += addedToCart;
          hierarchy[cat1].children[cat2].totals.totalRevenue += revenue;
          hierarchy[cat1].children[cat2].totals.totalItemsPurchased += purchases;
          hierarchy[cat1].children[cat2].totals.totalItemsViewed += views;
          hierarchy[cat1].children[cat2].totals.totalItemsAddedToCart += addedToCart;
        }
      });
      
      // Calculate conversion rates for hierarchy
      Object.keys(hierarchy).forEach(cat1 => {
        Object.keys(hierarchy[cat1].months).forEach(month => {
          const m = hierarchy[cat1].months[month];
          m.conversionRate = m.totalItemsViewed > 0 ? (m.totalItemsPurchased / m.totalItemsViewed) : 0;
          m.addToCartRate = m.totalItemsViewed > 0 ? (m.totalItemsAddedToCart / m.totalItemsViewed) : 0;
        });
        hierarchy[cat1].totals.conversionRate = hierarchy[cat1].totals.totalItemsViewed > 0
          ? (hierarchy[cat1].totals.totalItemsPurchased / hierarchy[cat1].totals.totalItemsViewed) : 0;
        
        Object.keys(hierarchy[cat1].children).forEach(cat2 => {
          Object.keys(hierarchy[cat1].children[cat2].months).forEach(month => {
            const m = hierarchy[cat1].children[cat2].months[month];
            m.conversionRate = m.totalItemsViewed > 0 ? (m.totalItemsPurchased / m.totalItemsViewed) : 0;
            m.addToCartRate = m.totalItemsViewed > 0 ? (m.totalItemsAddedToCart / m.totalItemsViewed) : 0;
          });
          hierarchy[cat1].children[cat2].totals.conversionRate = hierarchy[cat1].children[cat2].totals.totalItemsViewed > 0
            ? (hierarchy[cat1].children[cat2].totals.totalItemsPurchased / hierarchy[cat1].children[cat2].totals.totalItemsViewed) : 0;
        });
      });
      
      // Apply search filter to hierarchy
      if (categorySearchTerm.trim()) {
        const search = categorySearchTerm.toLowerCase().trim();
        const filteredHierarchy = {};
        Object.entries(hierarchy).forEach(([cat1, data]) => {
          if (cat1.toLowerCase().includes(search)) {
            filteredHierarchy[cat1] = data;
          } else {
            // Check if any child matches
            const matchingChildren = {};
            Object.entries(data.children).forEach(([cat2, childData]) => {
              if (cat2.toLowerCase().includes(search)) {
                matchingChildren[cat2] = childData;
              }
            });
            if (Object.keys(matchingChildren).length > 0) {
              filteredHierarchy[cat1] = { ...data, children: matchingChildren };
            }
          }
        });
        hierarchicalCategoryData = filteredHierarchy;
      } else {
        hierarchicalCategoryData = hierarchy;
      }
    }

    return { 
      months: sortedMonths, 
      dimensionData: filteredDimensionData, 
      monthlyTotals: filteredMonthlyTotals, 
      dimensionTotals: filteredDimensionTotals, 
      hierarchicalCategoryData,
      isLoading: false 
    }; 
  // Update dependencies to include all search terms and loading flags for filtering
  }, [
    monthlyBrandData,
    categoryPivotDataAll,
    productPivotData,
    weeklyBrandData,
    weeklyCategoryData,
    selectedDimension,
    pivotGranularity,
    brandSearchTerm,
    categorySearchTerm,
    productSearchTerm,
    isLoadingMonthlyData,
    isLoadingCategoryPivotDataAll,
    isLoadingProductPivotData,
    isLoadingWeeklyBrandData,
    isLoadingWeeklyCategoryData
  ]);

  // --- Pivot table sorting & total column helpers ---
  const metricRawValue = (bucket) => {
    if (!bucket) return 0;
    if (selectedTableMetric === 'conversionRate') {
      return bucket.conversionRate ?? (bucket.totalItemsViewed > 0 ? bucket.totalItemsPurchased / bucket.totalItemsViewed : 0);
    }
    return bucket[selectedTableMetric] ?? 0;
  };

  // Renders the "Total" column cell the same way the month cells are rendered
  const renderTotalCellContent = (bucket) => {
    const value = metricRawValue(bucket);
    if (selectedTableMetric === 'totalItemsAddedToCart') {
      const views = bucket?.totalItemsViewed || 0;
      const rate = views > 0 ? (bucket?.totalItemsAddedToCart || 0) / views : 0;
      if (value <= 0 && rate <= 0) return '-';
      return (
        <div className="flex flex-col items-end leading-tight">
          <span>{formatNumber(value)}</span>
          <span className="text-xs text-gray-500">{(rate * 100).toFixed(2)}%</span>
        </div>
      );
    }
    if (value === 0) return '-';
    return selectedTableMetric === 'totalRevenue'
      ? formatCurrencyCompact(value)
      : selectedTableMetric === 'conversionRate'
        ? `${(value * 100).toFixed(2)}%`
        : formatNumber(value);
  };

  const togglePivotSort = (key) => {
    setPivotSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );
    setDisplayedRowCount(50);
  };

  const SortArrow = ({ columnKey }) => (
    <span className={`ml-1 text-[10px] ${pivotSort.key === columnKey ? 'text-blue-600' : 'text-gray-300'}`}>
      {pivotSort.key === columnKey ? (pivotSort.dir === 'asc' ? '\u25b2' : '\u25bc') : '\u25bc'}
    </span>
  );

  // entries: [name, node]; accessors read totals / a period bucket off the node
  const sortPivotEntries = (entries, getTotals, getPeriodBucket) => {
    const dir = pivotSort.dir === 'asc' ? 1 : -1;
    const list = [...entries];

    if (pivotSort.key === 'name') {
      return list.sort(([a], [b]) => dir * String(a).localeCompare(String(b), 'tr'));
    }

    if (pivotSort.key === 'default') {
      // Original behaviour: Product by latest period, others by total revenue (or CR)
      return list.sort(([nameA, nodeA], [nameB, nodeB]) => {
        if (selectedDimension === 'Product' && pivotTableData.months.length > 0) {
          const latest = pivotTableData.months[pivotTableData.months.length - 1];
          return metricRawValue(getPeriodBucket(nameB, nodeB, latest)) - metricRawValue(getPeriodBucket(nameA, nodeA, latest));
        }
        if (selectedTableMetric === 'conversionRate') {
          return (getTotals(nameB, nodeB)?.conversionRate ?? 0) - (getTotals(nameA, nodeA)?.conversionRate ?? 0);
        }
        return (getTotals(nameB, nodeB)?.totalRevenue ?? 0) - (getTotals(nameA, nodeA)?.totalRevenue ?? 0);
      });
    }

    if (pivotSort.key === 'total') {
      return list.sort(([nameA, nodeA], [nameB, nodeB]) =>
        dir * (metricRawValue(getTotals(nameA, nodeA)) - metricRawValue(getTotals(nameB, nodeB))));
    }

    return list.sort(([nameA, nodeA], [nameB, nodeB]) =>
      dir * (metricRawValue(getPeriodBucket(nameA, nodeA, pivotSort.key)) - metricRawValue(getPeriodBucket(nameB, nodeB, pivotSort.key))));
  };

  const flatTotalsOf = (name) => pivotTableData.dimensionTotals?.[name];
  const flatBucketOf = (name, monthlyData, period) => monthlyData?.[period];
  const nodeTotalsOf = (name, node) => node?.totals;
  const nodeBucketOf = (name, node, period) => node?.months?.[period];

  // Grand total across all periods (respects the active search filter)
  const pivotGrandTotal = useMemo(() => {
    const acc = { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 };
    const source = (pivotTableData.hierarchicalCategoryData && selectedDimension === 'Category')
      ? Object.values(pivotTableData.hierarchicalCategoryData).map(n => n.totals)
      : Object.values(pivotTableData.dimensionTotals || {});
    source.forEach(t => {
      acc.totalRevenue += t?.totalRevenue || 0;
      acc.totalItemsPurchased += t?.totalItemsPurchased || 0;
      acc.totalItemsViewed += t?.totalItemsViewed || 0;
      acc.totalItemsAddedToCart += t?.totalItemsAddedToCart || 0;
    });
    acc.conversionRate = acc.totalItemsViewed > 0 ? acc.totalItemsPurchased / acc.totalItemsViewed : 0;
    return acc;
  }, [pivotTableData, selectedDimension]);

  // --- Excel Export (pivot table, all rows) ---
  const METRIC_LABELS = {
    totalRevenue: 'Ciro',
    totalItemsPurchased: 'Satın Alma',
    totalItemsViewed: 'Ürün Görüntüleme',
    totalItemsAddedToCart: 'Sepete Ekleme',
    conversionRate: 'CR (%)'
  };

  const periodLabel = (key) =>
    key.includes('W')
      ? key // 2026-W37
      : new Date(key + '-01T00:00:00Z').toLocaleString('default', { month: 'short', year: 'numeric', timeZone: 'UTC' });

  const metricValue = (bucket, metric) => {
    const raw = bucket?.[metric] ?? 0;
    if (metric === 'conversionRate') return Number(((raw || 0) * 100).toFixed(2));
    return Number(raw) || 0;
  };

  const handleExportExcel = () => {
    const { months, dimensionData, dimensionTotals, monthlyTotals, hierarchicalCategoryData } = pivotTableData;
    const isHierarchical = !!(hierarchicalCategoryData && selectedDimension === 'Category');

    if (pivotTableData.isLoading) return;
    if (!isHierarchical && Object.keys(dimensionData || {}).length === 0) return;

    const metric = selectedTableMetric;
    const metricLabel = METRIC_LABELS[metric] || metric;
    const periodHeaders = months.map(periodLabel);

    // ---- Sheet 1: pivot (dimension x period) for the selected metric ----
    const pivotRows = [[selectedDimension, `Toplam ${metricLabel}`, ...periodHeaders]];

    if (isHierarchical) {
      sortPivotEntries(Object.entries(hierarchicalCategoryData), nodeTotalsOf, nodeBucketOf)
        .forEach(([cat1, node]) => {
          pivotRows.push([
            cat1,
            metricValue(node.totals, metric),
            ...months.map((m) => metricValue(node.months?.[m], metric))
          ]);
          sortPivotEntries(Object.entries(node.children || {}), nodeTotalsOf, nodeBucketOf)
            .forEach(([cat2, child]) => {
              pivotRows.push([
                `    ${cat1} > ${cat2}`,
                metricValue(child.totals, metric),
                ...months.map((m) => metricValue(child.months?.[m], metric))
              ]);
            });
        });
    } else {
      // Same ordering the table uses (including the column the user sorted by)
      const sortedEntries = sortPivotEntries(Object.entries(dimensionData), flatTotalsOf, flatBucketOf);

      sortedEntries.forEach(([name, monthlyData]) => {
        pivotRows.push([
          name,
          metricValue(dimensionTotals[name], metric),
          ...months.map((m) => metricValue(monthlyData[m], metric))
        ]);
      });
    }

    // Footer totals
    const grand = Object.values(monthlyTotals || {}).reduce(
      (acc, m) => {
        acc.totalRevenue += m.totalRevenue || 0;
        acc.totalItemsPurchased += m.totalItemsPurchased || 0;
        acc.totalItemsViewed += m.totalItemsViewed || 0;
        acc.totalItemsAddedToCart += m.totalItemsAddedToCart || 0;
        return acc;
      },
      { totalRevenue: 0, totalItemsPurchased: 0, totalItemsViewed: 0, totalItemsAddedToCart: 0 }
    );
    grand.conversionRate = grand.totalItemsViewed > 0 ? grand.totalItemsPurchased / grand.totalItemsViewed : 0;
    pivotRows.push([
      'TOPLAM',
      metricValue(grand, metric),
      ...months.map((m) => metricValue(monthlyTotals?.[m], metric))
    ]);

    const wsPivot = XLSX.utils.aoa_to_sheet(pivotRows);
    wsPivot['!cols'] = [{ wch: 42 }, { wch: 16 }, ...periodHeaders.map(() => ({ wch: 13 }))];
    wsPivot['!freeze'] = { xSplit: 1, ySplit: 1 };

    // ---- Sheet 2: all metrics side by side, for ranking ----
    const summarySource = isHierarchical
      ? Object.fromEntries(Object.entries(hierarchicalCategoryData).map(([k, v]) => [k, v.totals]))
      : dimensionTotals;

    const summaryRows = [[selectedDimension, 'Ciro', 'Satın Alma', 'Ürün Görüntüleme', 'Sepete Ekleme', 'CR (%)', 'Sepete Ekleme Oranı (%)']];
    Object.entries(summarySource || {})
      .sort(([, a], [, b]) => (b?.totalRevenue ?? 0) - (a?.totalRevenue ?? 0))
      .forEach(([name, t]) => {
        const views = t?.totalItemsViewed || 0;
        const purchases = t?.totalItemsPurchased || 0;
        const carts = t?.totalItemsAddedToCart || 0;
        summaryRows.push([
          name,
          Number(t?.totalRevenue || 0),
          purchases,
          views,
          carts,
          views > 0 ? Number(((purchases / views) * 100).toFixed(2)) : 0,
          views > 0 ? Number(((carts / views) * 100).toFixed(2)) : 0
        ]);
      });
    summaryRows.push([
      'TOPLAM',
      Number(grand.totalRevenue),
      grand.totalItemsPurchased,
      grand.totalItemsViewed,
      grand.totalItemsAddedToCart,
      grand.totalItemsViewed > 0 ? Number(((grand.totalItemsPurchased / grand.totalItemsViewed) * 100).toFixed(2)) : 0,
      grand.totalItemsViewed > 0 ? Number(((grand.totalItemsAddedToCart / grand.totalItemsViewed) * 100).toFixed(2)) : 0
    ]);

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 42 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 20 }];
    wsSummary['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: summaryRows.length - 2, c: 6 } }) };
    wsSummary['!freeze'] = { xSplit: 1, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet');
    XLSX.utils.book_append_sheet(wb, wsPivot, `${metricLabel}`.slice(0, 31));

    const stamp = new Date().toISOString().slice(0, 10);
    const granLabel = pivotGranularity === 'weekly' ? 'haftalik' : 'aylik';
    XLSX.writeFile(wb, `urun-performans-${selectedDimension.toLowerCase()}-${granLabel}-${stamp}.xlsx`);
  };

  // --- End Data Transformation ---

  return (
    <div className="p-6 space-y-6">
      {/* Header - Updated to match GA4Overview style */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-full overflow-x-hidden">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <img 
              src="/Google-Analytics-Logo.png" 
              alt="Analytics Icon" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Product Performance</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Filter Buttons */}
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'yesterday'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('yesterday')}
              >
                Yesterday
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last7'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last7')}
              >
                7 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last30'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last30')}
              >
                30 Days
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'thisMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('thisMonth')}
              >
                This Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'lastMonth'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('lastMonth')}
              >
                Last Month
              </button>
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'last90'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange('last90')}
              >
                90 Days
              </button>
              <button
                className={`flex items-center gap-1 px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => {
                  setDateRange('custom');
                  setShowDatePicker(true);
                }}
              >
                <Calendar className="w-3.5 h-3.5" />
                Custom
              </button>
            </div>
            
            <button className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleExportExcel}
              disabled={pivotTableData.isLoading}
              title={`${selectedDimension} tablosunu Excel'e aktar`}
              className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Custom Date Range Display */}
        {dateRange === 'custom' && customDateRange.startDate && customDateRange.endDate && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Custom Date Range:</span>
              <span className="text-sm text-blue-700">
                {new Date(customDateRange.startDate).toLocaleDateString('en-US', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              <span className="text-sm text-blue-600">-</span>
              <span className="text-sm text-blue-700">
                {new Date(customDateRange.endDate).toLocaleDateString('en-US', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-lg ml-2">
                {(() => {
                  const start = new Date(customDateRange.startDate);
                  const end = new Date(customDateRange.endDate);
                  const diffTime = Math.abs(end - start);
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                  return `${diffDays} days`;
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Summary Cards - Changed to 5 columns */}
        <div className="grid grid-cols-5 gap-4"> {/* Changed grid-cols-6 to grid-cols-5 */} 
          {/* Total Items Viewed Card */}
          <Card 
            className={`p-4 rounded-xl shadow-xl border cursor-pointer transition-all duration-200 ${ 
              activeCard === 'total_items_viewed' // Updated activeCard key
                ? 'bg-gradient-to-br from-teal-500 to-teal-600 text-white border-teal-400' // Changed color scheme
                : 'bg-white border-gray-100 hover:bg-teal-50'
            }`}
            onClick={() => handleCardClick('total_items_viewed')} // Updated activeCard key
          >
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${activeCard === 'total_items_viewed' ? 'text-teal-100' : 'text-gray-500'}`}>Total Items Viewed</p>
                <p className="text-xl font-semibold mt-1">
                  {isLoadingDailyProductData ? '--' : formatNumber(dailyProductData.totals.totalItemsViewed)}
                </p>
                {/* Trend removed per request */}
              </div>
              <div className={`p-2 rounded-lg ${activeCard === 'total_items_viewed' ? 'bg-white/10' : 'bg-teal-50'}`}>
                <Eye className={`w-4 h-4 ${activeCard === 'total_items_viewed' ? '' : 'text-teal-600'}`} /> {/* Changed icon */} 
              </div>
            </div>
          </Card>

          {/* Total Revenue Card - Updated with API Data & Trend */}
          <Card 
            className={`p-4 rounded-xl shadow-xl border cursor-pointer transition-all duration-200 ${ 
              activeCard === 'total_revenue' 
                ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-400' 
                : 'bg-white border-gray-100 hover:bg-orange-50'
            }`}
            onClick={() => handleCardClick('total_revenue')}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${activeCard === 'total_revenue' ? 'text-orange-100' : 'text-gray-500'}`}>Total Revenue</p>
                <p className="text-xl font-semibold mt-1">
                  {isLoadingDailyProductData ? '--' : formatCurrency(dailyProductData.totals.totalRevenue, 0)}
                </p>
                {/* Trend removed per request */}
              </div>
              <div className={`p-2 rounded-lg ${activeCard === 'total_revenue' ? 'bg-white/10' : 'bg-orange-50'}`}>
                <DollarSign className={`w-4 h-4 ${activeCard === 'total_revenue' ? '' : 'text-orange-600'}`} />
              </div>
            </div>
          </Card>

          {/* Average Order Value Card - Updated with API Data & Trend */}
          <Card 
            className={`p-4 rounded-xl shadow-xl border cursor-pointer transition-all duration-200 ${ 
              activeCard === 'avg_order_value' 
                ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-400' 
                : 'bg-white border-gray-100 hover:bg-purple-50'
            }`}
            onClick={() => handleCardClick('avg_order_value')}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${activeCard === 'avg_order_value' ? 'text-purple-100' : 'text-gray-500'}`}>Avg. Order Value</p>
                <p className="text-xl font-semibold mt-1">
                  {isLoadingDailyProductData || dailyProductData.totals.totalItemsPurchased === 0 
                    ? '--' 
                    : formatCurrency(dailyProductData.totals.avgOrderValue, 0)} {/* Use calculated AOV */} 
                </p>
                {/* Trend removed per request */}
              </div>
              <div className={`p-2 rounded-lg ${activeCard === 'avg_order_value' ? 'bg-white/10' : 'bg-purple-50'}`}>
                <TrendingUp className={`w-4 h-4 ${activeCard === 'avg_order_value' ? '' : 'text-purple-600'}`} />
              </div>
            </div>
          </Card>

          {/* Units Sold Card - Updated with API Data & Trend */} 
          <Card 
            className={`p-4 rounded-xl shadow-xl border cursor-pointer transition-all duration-200 ${ 
              activeCard === 'units_sold' 
                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white border-green-400' 
                : 'bg-white border-gray-100 hover:bg-green-50'
            }`}
            onClick={() => handleCardClick('units_sold')}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${activeCard === 'units_sold' ? 'text-green-100' : 'text-gray-500'}`}>Units Sold</p>
                <p className="text-xl font-semibold mt-1">
                  {isLoadingDailyProductData ? '--' : formatNumber(dailyProductData.totals.totalItemsPurchased)}
                </p>
                {/* Trend removed per request */}
              </div>
              <div className={`p-2 rounded-lg ${activeCard === 'units_sold' ? 'bg-white/10' : 'bg-green-50'}`}>
                <ShoppingCart className={`w-4 h-4 ${activeCard === 'units_sold' ? '' : 'text-green-600'}`} />
              </div>
            </div>
          </Card>

          {/* Conversion Rate Card - Updated with API Data & Trend */} 
          <Card 
            className={`p-4 rounded-xl shadow-xl border cursor-pointer transition-all duration-200 ${ 
              activeCard === 'conversion_rate' 
                ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-indigo-400' 
                : 'bg-white border-gray-100 hover:bg-indigo-50'
            }`}
            onClick={() => handleCardClick('conversion_rate')}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className={`text-xs ${activeCard === 'conversion_rate' ? 'text-indigo-100' : 'text-gray-500'}`}>Conversion Rate</p>
                <p className="text-xl font-semibold mt-1">
                  {isLoadingDailyProductData ? '--' : `${(dailyProductData.totals.conversionRate * 100).toFixed(2)}%`}
                </p>
                {/* Trend removed per request */}
              </div>
              <div className={`p-2 rounded-lg ${activeCard === 'conversion_rate' ? 'bg-white/10' : 'bg-indigo-50'}`}>
                <Target className={`w-4 h-4 ${activeCard === 'conversion_rate' ? '' : 'text-indigo-600'}`} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Grafik Bölümü */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top 10 Products Placeholder (Remains col-span-1) */}
        <Card className="col-span-1 p-6 rounded-2xl">
          <div className="flex justify-between items-center mb-6">
            {/* Update Title to reflect dateRange */}
            <h3 className="text-lg font-semibold text-gray-800">
              Top 10 Products
              <span className="ml-2 text-sm text-gray-500 font-normal">
                ({ // Update text logic for new buttons
                  dateRange === 'today' ? 'Today' : 
                  dateRange === 'yesterday' ? 'Yesterday' : 
                  dateRange === 'thisMonth' ? 'This Month' : 
                  dateRange === 'last7' ? 'Last 7 Days' : 
                  dateRange === 'last30' ? 'Last 30 Days' : 
                  dateRange === 'last90' ? 'Last 90 Days' : 
                  'Selected Range' // Fallback
                })
              </span>
            </h3>
            {/* Removed dropdown/button */}
          </div>
          
          <div className="h-64 overflow-y-auto pr-2"> {/* Keep overflow and padding */} 
            {/* Conditional Rendering based on loading and data presence */} 
            {isLoadingTop10DailyData ? (
              <div className="flex justify-center items-center h-full">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
              </div>
            ) : top10DailyData && top10DailyData.length > 0 ? (
              <ol className="space-y-3"> {/* List container */} 
                {top10DailyData
                  // Sorting should now be handled by backend, but keep slice just in case
                  .slice(0, 10)
                  .map((item, index) => (
                    <li key={item.itemId || index} className="text-sm text-gray-700 py-2 px-1 odd:bg-gray-50 rounded-md">
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-6 text-right">{index + 1}.</span>
                          <span className="font-medium truncate" title={item.itemName || 'Unknown Product'}>
                            {(item.itemName || 'Unknown Product').length > 35
                              ? (item.itemName || 'Unknown Product').substring(0, 35) + '...'
                              : (item.itemName || 'Unknown Product')}
                          </span>
                        </div>
                        <span className="text-gray-900 font-semibold whitespace-nowrap">
                          {formatCurrency(item.totalRevenue || 0, 0)}
                        </span>
                      </div>
                    </li>
                  ))}
              </ol>
            ) : (
              <div className="flex flex-col justify-center items-center h-full text-center text-gray-500">
                 <Package className="h-10 w-10 text-gray-400 mb-2" />
                 {/* Update no-data message */}
                 <p className="text-sm">
                  No top product data available for the selected period ({ // Update text logic
                    dateRange === 'today' ? 'Today' : 
                    dateRange === 'yesterday' ? 'Yesterday' : 
                    dateRange === 'thisMonth' ? 'This Month' : 
                    dateRange === 'last7' ? 'Last 7 Days' : 
                    dateRange === 'last30' ? 'Last 30 Days' : 
                    dateRange === 'last90' ? 'Last 90 Days' : 
                    'Selected Range' // Fallback
                  }).
                 </p>
              </div>
            )}
          </div>
        </Card>

        {/* Revenue Trend Chart - Expanded to col-span-2 */}
        <Card className="lg:col-span-2 p-6 rounded-2xl"> {/* Added lg:col-span-2 */} 
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {/* Title logic based on selectedMetric */} 
              {selectedMetric === 'total_products' && "Products Trend"}
              {selectedMetric === 'total_revenue' && "Revenue Trend"}
              {selectedMetric === 'avg_order_value' && "Avg. Order Value Trend"}
              {selectedMetric === 'units_sold' && "Units Sold Trend"}
              {selectedMetric === 'conversion_rate' && "Conversion Rate Trend"}
              {selectedMetric === 'avg_price' && "Avg. Price Trend"}
              {/* Dynamically update date range in title */} 
              <span className="ml-2 text-sm text-gray-500 font-normal">
                ({ // Update text logic
                  dateRange === 'today' ? 'Today' : 
                  dateRange === 'yesterday' ? 'Yesterday' : 
                  dateRange === 'thisMonth' ? 'This Month' : 
                  dateRange === 'last7' ? 'Last 7 Days' : 
                  dateRange === 'last30' ? 'Last 30 Days' : 
                  dateRange === 'last90' ? 'Last 90 Days' : 
                  'Selected Range' // Fallback
                })
              </span>
            </h3>
            <select 
              className="text-sm border rounded-lg p-1"
              value={selectedMetric}
              onChange={(e) => {
                setSelectedMetric(e.target.value);
                setActiveCard(e.target.value); // Keep active card sync
              }}
            >
              {/* Options */}
              <option value="total_products">Products</option>
              <option value="total_revenue">Revenue</option>
              <option value="avg_order_value">Avg. Order Value</option>
              <option value="units_sold">Units Sold</option>
              <option value="conversion_rate">Conversion Rate</option>
            </select>
          </div>
          
          <div className="h-64">
            {/* Use the memoized chart data */}
            <LineChart 
              data={revenueTrendChartData} 
              options={{
                plugins: {
                  legend: {
                    display: false
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.raw;
                        if (selectedMetric === 'total_revenue' || selectedMetric === 'avg_order_value' || selectedMetric === 'avg_price') {
                          return `${context.dataset.label}: ${formatCurrency(value)}`;
                        } else if (selectedMetric === 'conversion_rate') {
                          return `${context.dataset.label}: ${value.toFixed(2)}%`;
                        } else {
                          return `${context.dataset.label}: ${formatNumber(value)}`;
                        }
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: false
                    },
                    ticks: {
                      maxTicksLimit: 7
                    }
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        if (selectedMetric === 'total_revenue' || selectedMetric === 'avg_order_value' || selectedMetric === 'avg_price') {
                          if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M ₺';
                          if (value >= 1000) return (value / 1000).toFixed(0) + 'K ₺';
                          return value + ' ₺';
                        } else if (selectedMetric === 'conversion_rate') {
                          return value + '%';
                        } else {
                          if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                          if (value >= 1000) return (value / 1000).toFixed(0) + 'K';
                          return value;
                        }
                      }
                    }
                  }
                },
                maintainAspectRatio: false
              }}
            />
          </div>
        </Card>
      </div>

      

      {/* --- New Pivot Table Section --- */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-4"> 
          {/* Left side: Dimension Buttons + Search Bar */}
          <div className="flex items-center gap-4"> 
            {/* Dimension Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedDimension('Brand')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${ 
                  selectedDimension === 'Brand' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                Brand
              </button>
              <button
                onClick={() => setSelectedDimension('Category')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${ 
                  selectedDimension === 'Category' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                Category
              </button>
              <button
                onClick={() => setSelectedDimension('Product')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${ 
                  selectedDimension === 'Product' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                Product
              </button>
            </div>

            {/* Search Bar - Show for all dimensions */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${selectedDimension === 'Category' ? 'categories' : selectedDimension.toLowerCase() + 's'}...`}
                value={
                  selectedDimension === 'Brand' ? brandSearchTerm :
                  selectedDimension === 'Category' ? categorySearchTerm :
                  productSearchTerm
                }
                onChange={(e) => {
                  if (selectedDimension === 'Brand') {
                    setBrandSearchTerm(e.target.value);
                  } else if (selectedDimension === 'Category') {
                    setCategorySearchTerm(e.target.value);
                  } else {
                    setProductSearchTerm(e.target.value);
                  }
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
              {(
                (selectedDimension === 'Brand' && brandSearchTerm) ||
                (selectedDimension === 'Category' && categorySearchTerm) ||
                (selectedDimension === 'Product' && productSearchTerm)
              ) && (
                <button
                  onClick={() => {
                    if (selectedDimension === 'Brand') {
                      setBrandSearchTerm('');
                    } else if (selectedDimension === 'Category') {
                      setCategorySearchTerm('');
                    } else {
                      setProductSearchTerm('');
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          {/* Right side: Metric Selector */}
          <div className="flex items-center gap-3"> 
            {/* Granularity Toggle */}
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              <button
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  pivotGranularity === 'weekly' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setPivotGranularity('weekly')}
              >
                Weekly
              </button>
              <button
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  pivotGranularity === 'monthly' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setPivotGranularity('monthly')}
              >
                Monthly
              </button>
            </div>
            {/* Metric Selector */}
            <label htmlFor="tableMetricSelect" className="text-sm font-medium text-gray-700">Metric:</label>
            <select
              id="tableMetricSelect"
              value={selectedTableMetric}
              onChange={(e) => setSelectedTableMetric(e.target.value)}
              className="pl-3 pr-8 py-1.5 bg-white border rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="totalRevenue">Revenue</option>
              <option value="totalItemsPurchased">Purchases</option>
              <option value="totalItemsViewed">Product View</option>
              <option value="totalItemsAddedToCart">Add to Cart</option>
              <option value="conversionRate">CR</option>
            </select>
          </div>
        </div>

        {/* Add wrapper for horizontal and vertical scrolling */}
        <div className="relative w-full max-w-full overflow-x-auto max-h-[650px] border border-gray-100 rounded-xl bg-white"> {/* Card-like inner container */} 
          <div className="min-w-full block align-middle">
            <table className={`${pivotGranularity === 'weekly' ? 'table-auto min-w-max' : 'table-fixed min-w-full'} divide-y divide-gray-200 border border-gray-200`}>
            <thead className="bg-gray-50 sticky top-0 z-10"> 
              <tr>
                <th
                  scope="col"
                  onClick={() => togglePivotSort('name')}
                  title="İsme göre sırala"
                  className="sticky left-0 bg-gray-50 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider z-30 cursor-pointer select-none hover:text-blue-600"
                >
                  {selectedDimension}
                  <SortArrow columnKey="name" />
                </th>
                {pivotTableData.months.length > 0 && (
                  <th
                    scope="col"
                    onClick={() => togglePivotSort('total')}
                    title="Toplama göre sırala"
                    className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap bg-gray-100 cursor-pointer select-none hover:text-blue-600 min-w-[7rem]"
                  >
                    Toplam
                    <SortArrow columnKey="total" />
                  </th>
                )}
                {/* Render month columns if months exist */}
                {pivotTableData.months.length > 0 && pivotTableData.months.map((month) => {
                  const isWeekKey = month.includes('W');
                  const label = isWeekKey
                    ? (month.split('-')[1] || month) // show only Wxx, remove year
                    : new Date(month + '-01T00:00:00Z').toLocaleString('default', { month: 'short', year: 'numeric', timeZone: 'UTC' });
                  return (
                    <th
                      key={month}
                      scope="col"
                      onClick={() => togglePivotSort(month)}
                      title={`${label} sütununa göre sırala`}
                      className={`px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-blue-600 ${pivotSort.key === month ? 'bg-blue-50 text-blue-700' : ''} ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}
                    >
                      {label}
                      <SortArrow columnKey={month} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            {/* Wrap tbody content in a scrollable div */} 
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Show loading based on selected dimension */}
              {pivotTableData.isLoading ? ( // Use isLoading from pivotTableData
                <tr>
                  {/* Adjust colspan based on number of months + 1 or just 1 if no months */}
                  <td colSpan={pivotTableData.months.length > 0 ? pivotTableData.months.length + 2 : 1} className="text-center py-10 text-gray-500">
                    <div className="flex justify-center items-center">
                       <Loader2 className="animate-spin h-5 w-5 mr-3" /> Loading data...
                    </div>
                  </td>
                </tr>
              ) : pivotTableData.hierarchicalCategoryData && selectedDimension === 'Category' ? (
                // Render hierarchical category rows for special brand
                sortPivotEntries(Object.entries(pivotTableData.hierarchicalCategoryData), nodeTotalsOf, nodeBucketOf)
                  .flatMap(([cat1Name, cat1Data]) => {
                    const isExpanded = expandedCategories.has(cat1Name);
                    const hasChildren = Object.keys(cat1Data.children || {}).length > 0;
                    const rows = [];
                    
                    // Parent row (Category 1)
                    rows.push(
                      <tr 
                        key={cat1Name} 
                        className={`hover:bg-gray-50 ${hasChildren ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (hasChildren) {
                            setExpandedCategories(prev => {
                              const next = new Set(prev);
                              if (next.has(cat1Name)) {
                                next.delete(cat1Name);
                              } else {
                                next.add(cat1Name);
                              }
                              return next;
                            });
                          }
                        }}
                      >
                        <td className="sticky left-0 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 z-30 max-w-xs truncate" title={cat1Name}>
                          <div className="flex items-center gap-2">
                            {hasChildren && (
                              isExpanded 
                                ? <ChevronDown className="w-4 h-4 text-blue-500" />
                                : <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            {!hasChildren && <span className="w-4" />}
                            <span className="font-semibold">{cat1Name}</span>
                            {hasChildren && (
                              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                {Object.keys(cat1Data.children).length}
                              </span>
                            )}
                          </div>
                        </td>
                        {pivotTableData.months.length > 0 && (
                          <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 bg-gray-50 min-w-[7rem] ${pivotSort.key === 'total' ? 'bg-blue-50' : ''}`}>
                            {renderTotalCellContent(cat1Data.totals)}
                          </td>
                        )}
                        {pivotTableData.months.map((month) => {
                          const value = cat1Data.months[month]?.[selectedTableMetric] ?? 0;
                          const displayValue =
                            selectedTableMetric === 'totalRevenue'
                              ? formatCurrencyCompact(value)
                              : selectedTableMetric === 'conversionRate'
                                ? `${((value || 0) * 100).toFixed(2)}%`
                                : formatNumber(value);
                          if (selectedTableMetric === 'totalItemsAddedToCart') {
                            const rate = cat1Data.months[month]?.addToCartRate ?? 0;
                            const rateText = `${(rate * 100).toFixed(2)}%`;
                            const hasAny = (value || 0) > 0 || (rate || 0) > 0;
                            return (
                              <td key={month} className={`px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 font-medium ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                                {hasAny ? (
                                  <div className="flex flex-col items-end leading-tight">
                                    <span>{formatNumber(value)}</span>
                                    <span className="text-xs text-gray-500">{rateText}</span>
                                  </div>
                                ) : '-'}
                              </td>
                            );
                          }
                          return (
                            <td key={month} className={`px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 font-medium ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                              {value === 0 ? '-' : displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                    
                    // Child rows (Category 2) - only if expanded
                    if (isExpanded && hasChildren) {
                      sortPivotEntries(Object.entries(cat1Data.children), nodeTotalsOf, nodeBucketOf)
                        .forEach(([cat2Name, cat2Data]) => {
                          rows.push(
                            <tr key={`${cat1Name}-${cat2Name}`} className="hover:bg-blue-50/30 bg-gray-50/50">
                              <td className="sticky left-0 bg-gray-50/50 px-6 py-3 whitespace-nowrap text-sm text-gray-700 z-30 max-w-xs truncate" title={cat2Name}>
                                <div className="flex items-center gap-2 pl-6">
                                  <span className="text-gray-300">└</span>
                                  <span>{cat2Name}</span>
                                </div>
                              </td>
                              {pivotTableData.months.length > 0 && (
                                <td className={`px-6 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-700 bg-gray-50 min-w-[7rem] ${pivotSort.key === 'total' ? 'bg-blue-50' : ''}`}>
                                  {renderTotalCellContent(cat2Data.totals)}
                                </td>
                              )}
                              {pivotTableData.months.map((month) => {
                                const value = cat2Data.months[month]?.[selectedTableMetric] ?? 0;
                                const displayValue =
                                  selectedTableMetric === 'totalRevenue'
                                    ? formatCurrencyCompact(value)
                                    : selectedTableMetric === 'conversionRate'
                                      ? `${((value || 0) * 100).toFixed(2)}%`
                                      : formatNumber(value);
                                if (selectedTableMetric === 'totalItemsAddedToCart') {
                                  const rate = cat2Data.months[month]?.addToCartRate ?? 0;
                                  const rateText = `${(rate * 100).toFixed(2)}%`;
                                  const hasAny = (value || 0) > 0 || (rate || 0) > 0;
                                  return (
                                    <td key={month} className={`px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600 ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                                      {hasAny ? (
                                        <div className="flex flex-col items-end leading-tight">
                                          <span>{formatNumber(value)}</span>
                                          <span className="text-xs text-gray-400">{rateText}</span>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  );
                                }
                                return (
                                  <td key={month} className={`px-6 py-3 whitespace-nowrap text-right text-sm text-gray-600 ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                                    {value === 0 ? '-' : displayValue}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        });
                    }
                    
                    return rows;
                  })
              ) : Object.keys(pivotTableData.dimensionData).length > 0 ? (
                 // Render data rows based on selected dimension (non-hierarchical)
                 // OPTIMIZED: Only render first N rows, with "Load More" option
                 (() => {
                   const sortedEntries = sortPivotEntries(
                     Object.entries(pivotTableData.dimensionData),
                     flatTotalsOf,
                     flatBucketOf
                   );
                   const totalRows = sortedEntries.length;
                   const displayedEntries = sortedEntries.slice(0, displayedRowCount);
                   const hasMoreRows = totalRows > displayedRowCount;
                   
                   return (
                     <>
                       {displayedEntries.map(([dimensionName, monthlyData]) => (
                  <tr key={dimensionName} className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 z-30 max-w-xs truncate" title={dimensionName}> 
                      {dimensionName}
                    </td>
                    {pivotTableData.months.length > 0 && (
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 bg-gray-50 min-w-[7rem] ${pivotSort.key === 'total' ? 'bg-blue-50' : ''}`}>
                        {renderTotalCellContent(pivotTableData.dimensionTotals[dimensionName])}
                      </td>
                    )}
                    {pivotTableData.months.map((month) => {
                      const value = monthlyData[month]?.[selectedTableMetric] ?? 0;
                      const displayValue =
                        selectedTableMetric === 'totalRevenue'
                          ? formatCurrencyCompact(value)
                          : selectedTableMetric === 'conversionRate'
                            ? `${((value || 0) * 100).toFixed(2)}%`
                            : formatNumber(value);
                      // When Add to Cart selected, also show Add to Cart Rate (add_to_cart / views)
                      if (selectedTableMetric === 'totalItemsAddedToCart') {
                        const rate = monthlyData[month]?.addToCartRate ?? 0;
                        const rateText = `${(rate * 100).toFixed(2)}%`;
                        const hasAny = (value || 0) > 0 || (rate || 0) > 0;
                        return (
                          <td key={month} className={`px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                            {hasAny ? (
                              <div className="flex flex-col items-end leading-tight">
                                <span>{formatNumber(value)}</span>
                                <span className="text-xs text-gray-500">{rateText}</span>
                              </div>
                            ) : '-'}
                          </td>
                        );
                      }
                      return (
                        <td key={month} className={`px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                          {value === 0 ? '-' : displayValue}
                        </td>
                      );
                    })}
                  </tr>
                       ))}
                       {/* Load More Row */}
                       {hasMoreRows && (
                         <tr>
                           <td 
                             colSpan={pivotTableData.months.length + 2} 
                             className="text-center py-4 bg-gray-50"
                           >
                             <button
                               onClick={() => setDisplayedRowCount(prev => prev + ROWS_PER_LOAD)}
                               className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                             >
                               Load More ({totalRows - displayedRowCount} remaining of {totalRows.toLocaleString()} total)
                             </button>
                             <span className="ml-4 text-xs text-gray-500">
                               Showing {displayedRowCount.toLocaleString()} of {totalRows.toLocaleString()} {selectedDimension.toLowerCase()}s
                             </span>
                           </td>
                         </tr>
                       )}
                     </>
                   );
                 })()
              ) : (
                 // Show message if no data for the selected dimension
                 <tr>
                  <td colSpan={pivotTableData.months.length > 0 ? pivotTableData.months.length + 2 : 1} className="text-center py-10 text-gray-500">
                    No data available for {selectedDimension} in the selected period.
                  </td>
                </tr>
              )}
            </tbody>
             {/* Footer with totals - only show if data exists */}
           {!pivotTableData.isLoading && (Object.keys(pivotTableData.dimensionData).length > 0 || pivotTableData.hierarchicalCategoryData) && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300 sticky bottom-0 z-20"> 
                  <tr>
                      <th scope="row" className="sticky left-0 bg-gray-100 px-6 py-4 text-left text-sm font-semibold text-gray-800 z-30"> 
                          TOTAL
                      </th>
                      {pivotTableData.months.length > 0 && (
                        <th scope="col" className="px-6 py-4 text-right text-sm font-bold text-gray-900 whitespace-nowrap bg-gray-200 min-w-[7rem]">
                          {renderTotalCellContent(pivotGrandTotal)}
                        </th>
                      )}
                      {/* Render footer totals if months exist */}
                      {pivotTableData.months.length > 0 && pivotTableData.months.map((month) => {
                          // For hierarchical category data, calculate totals from hierarchy
                          let totalValue = 0;
                          let totalViews = 0;
                          let totalAddToCart = 0;
                          let totalPurchases = 0;
                          
                          if (pivotTableData.hierarchicalCategoryData && selectedDimension === 'Category') {
                            Object.values(pivotTableData.hierarchicalCategoryData).forEach(cat1Data => {
                              const monthData = cat1Data.months[month];
                              if (monthData) {
                                totalValue += monthData[selectedTableMetric] ?? 0;
                                totalViews += monthData.totalItemsViewed ?? 0;
                                totalAddToCart += monthData.totalItemsAddedToCart ?? 0;
                                totalPurchases += monthData.totalItemsPurchased ?? 0;
                              }
                            });
                            // Recalculate rates for aggregated totals
                            if (selectedTableMetric === 'conversionRate') {
                              totalValue = totalViews > 0 ? (totalPurchases / totalViews) : 0;
                            }
                          } else {
                            totalValue = pivotTableData.monthlyTotals[month]?.[selectedTableMetric] ?? 0;
                            totalViews = pivotTableData.monthlyTotals[month]?.totalItemsViewed ?? 0;
                            totalAddToCart = pivotTableData.monthlyTotals[month]?.totalItemsAddedToCart ?? 0;
                          }
                          
                          const displayTotal =
                              selectedTableMetric === 'totalRevenue'
                                  ? formatCurrencyCompact(totalValue)
                                  : selectedTableMetric === 'conversionRate'
                                    ? `${((totalValue || 0) * 100).toFixed(2)}%`
                                    : formatNumber(totalValue);
                          if (selectedTableMetric === 'totalItemsAddedToCart') {
                            const rate = totalViews > 0 ? (totalAddToCart / totalViews) : 0;
                            const rateText = `${(rate * 100).toFixed(2)}%`;
                            const hasAny = (totalValue || 0) > 0 || (rate || 0) > 0;
                            return (
                              <th key={month} scope="col" className={`px-6 py-4 text-right text-sm font-semibold text-gray-800 whitespace-nowrap ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                                {hasAny ? (
                                  <div className="flex flex-col items-end leading-tight">
                                    <span>{formatNumber(totalValue)}</span>
                                    <span className="text-xs text-gray-600">{rateText}</span>
                                  </div>
                                ) : '-'}
                              </th>
                            );
                          }
                          return (
                              <th key={month} scope="col" className={`px-6 py-4 text-right text-sm font-semibold text-gray-800 whitespace-nowrap ${pivotGranularity === 'weekly' ? 'min-w-[6rem] w-24' : ''}`}>
                                  {totalValue === 0 ? '-' : displayTotal}
                              </th>
                          );
                      })}
                  </tr>
              </tfoot>
             )}
            </table>
          </div>
        </div>
      </div>
      {/* --- End New Pivot Table Section --- */}

      {/* --- Product Insights Section --- */}
      {(() => {
        const insightColorClasses = {
          green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', badge: 'bg-green-100 text-green-700', headerBg: 'bg-green-100' },
          blue:  { bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-600',  badge: 'bg-blue-100 text-blue-700',  headerBg: 'bg-blue-100' },
          red:   { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-600',   badge: 'bg-red-100 text-red-700',   headerBg: 'bg-red-100' },
          amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', headerBg: 'bg-amber-100' },
        };

        const insightCards = [
          { key: 'newSalesConverts', color: 'green', Icon: TrendingUp,
            columns: [
              { label: 'Brand', accessor: r => r.item_brand },
              { label: 'Product ID', accessor: r => r.item_id },
              { label: 'Product Name', accessor: r => r.item_name },
              { label: 'Sales', accessor: r => formatNumber(r.current_purchases), className: 'text-right' },
              { label: 'Revenue', accessor: r => formatCurrencyCompact(r.current_revenue), className: 'text-right' },
              { label: 'Views', accessor: r => formatNumber(r.current_views), className: 'text-right' },
            ]},
          { key: 'viewedThenConverted', color: 'blue', Icon: Eye,
            columns: [
              { label: 'Brand', accessor: r => r.item_brand },
              { label: 'Product ID', accessor: r => r.item_id },
              { label: 'Product Name', accessor: r => r.item_name },
              { label: 'Prev. Month Views', accessor: r => formatNumber(r.previous_views), className: 'text-right' },
              { label: 'This Month Sales', accessor: r => formatNumber(r.current_purchases), className: 'text-right' },
              { label: 'Revenue', accessor: r => formatCurrencyCompact(r.current_revenue), className: 'text-right' },
            ]},
          { key: 'lostSalesProducts', color: 'red', Icon: ShoppingCart,
            columns: [
              { label: 'Brand', accessor: r => r.item_brand },
              { label: 'Product ID', accessor: r => r.item_id },
              { label: 'Product Name', accessor: r => r.item_name },
              { label: 'Prev. Month Sales', accessor: r => formatNumber(r.previous_purchases), className: 'text-right' },
              { label: 'Lost Revenue', accessor: r => formatCurrencyCompact(r.lost_revenue), className: 'text-right' },
              { label: 'This Month Views', accessor: r => formatNumber(r.current_views), className: 'text-right' },
            ]},
          { key: 'highViewLowConversion', color: 'amber', Icon: BarChart2,
            columns: [
              { label: 'Brand', accessor: r => r.item_brand },
              { label: 'Product ID', accessor: r => r.item_id },
              { label: 'Product Name', accessor: r => r.item_name },
              { label: 'Views', accessor: r => formatNumber(r.total_views), className: 'text-right' },
              { label: 'Sales', accessor: r => formatNumber(r.total_purchases), className: 'text-right' },
              { label: 'Conv. Rate', accessor: r => `${(r.conversion_rate * 100).toFixed(2)}%`, className: 'text-right' },
            ]},
        ];

        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Product Insights
              {productInsights?.meta && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {productInsights.meta.previousMonth} vs {productInsights.meta.currentMonth}
                </span>
              )}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {insightCards.map(({ key, color, Icon, columns }) => {
                const colors = insightColorClasses[color];
                const insight = productInsights?.insights?.[key];
                const isExpanded = expandedInsightCards.has(key);
                const items = insight?.items || [];

                return (
                  <div key={key} className={`rounded-xl border ${colors.border} overflow-hidden`}>
                    <div
                      className={`${colors.headerBg} px-4 py-3 cursor-pointer select-none`}
                      onClick={() => {
                        setExpandedInsightCards(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) { next.delete(key); } else { next.add(key); }
                          return next;
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isExpanded
                            ? <ChevronDown className={`w-4 h-4 ${colors.text}`} />
                            : <ChevronRight className={`w-4 h-4 ${colors.text}`} />
                          }
                          <Icon className={`w-5 h-5 ${colors.text}`} />
                          <span className="font-semibold text-gray-800">
                            {isLoadingInsights ? 'Loading...' : (insight?.title || key)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isLoadingInsights ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          ) : (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
                              {insight?.count || 0} products
                            </span>
                          )}
                        </div>
                      </div>
                      {insight?.description && (
                        <p className="text-xs text-gray-500 mt-1 ml-11">{insight.description}</p>
                      )}
                    </div>

                    {isExpanded && (
                      <div className={`${colors.bg} px-4 py-3`}>
                        {isLoadingInsights ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                          </div>
                        ) : items.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No products found in this category</p>
                        ) : (
                          <div className="overflow-auto max-h-80">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  {columns.map((col, i) => (
                                    <th key={i} className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase ${col.className || ''}`}>
                                      {col.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((row, idx) => (
                                  <tr key={idx} className="border-b border-gray-100 hover:bg-white/50">
                                    {columns.map((col, i) => (
                                      <td key={i} className={`px-3 py-2 text-gray-700 whitespace-nowrap truncate max-w-[200px] ${col.className || ''}`} title={String(col.accessor(row))}>
                                        {col.accessor(row)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {/* --- End Product Insights Section --- */}

      {/* Custom Date Range Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Select Date Range</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={customDateRange.startDate || ''}
                  onChange={(e) => setCustomDateRange(prev => ({
                    ...prev,
                    startDate: e.target.value
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">End Date</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 border rounded-lg"
                  value={customDateRange.endDate || ''}
                  onChange={(e) => setCustomDateRange(prev => ({
                    ...prev,
                    endDate: e.target.value
                  }))}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  onClick={() => {
                    setShowDatePicker(false);
                    setDateRange('last30'); // Reset to default if cancelled
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  onClick={() => {
                    if (customDateRange.startDate && customDateRange.endDate) {
                      // Custom date range'i uygula
                      setShowDatePicker(false);
                      console.log('Applying custom date range:', customDateRange.startDate, 'to', customDateRange.endDate);
                      // Date range zaten 'custom' olarak set edilmiş durumda, API'ler otomatik çağrılacak
                    } else {
                      alert('Please select both start and end dates');
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}